<template>
  <div class="metric-chart-wrapper" :class="{ 'full-screen': isFullscreen }" ref="metricGraph">
    <div class="metric-chart-meta">
      <div class="metric-chart-name">
        <h5>{{graph.name}}</h5>
      </div>
      <div class="metric-chart-actions">
        <b-button @click="goFullScreen" class="button is-primary is-outlined">
          <font-awesome-icon :icon="['far', 'window-restore']" :title="isFullscreen ? 'Minimize' : 'Full Screen'" class="has-cursor-pointer" />
        </b-button>
        <b-button @click="removeGraph(graph)" class="button is-primary is-outlined">
          <font-awesome-icon :icon="['far', 'trash-alt']" title="Remove Graph" class="has-cursor-pointer" />
        </b-button>        
        <b-button @click="exportGraph" class="button is-primary is-outlined" :disabled="!isExportEnabled">
          <font-awesome-icon v-if="!isExporting" :icon="['far', 'download']" title="Export Graph" class="has-cursor-pointer" />
          <font-awesome-icon v-else :icon="['far', 'spinner']" title="Exporting..." class="fa-spin" />
        </b-button>
        <b-button @click="toggleOptions" class="button is-primary is-outlined">
          <font-awesome-icon :icon="['far', 'cog']" title="Options Menu" class="has-cursor-pointer" />
        </b-button>
      </div>
    </div>
    <div class="card metric-chart-dropdown" v-if="optionsVisible">
      <table>
        <tbody>
          <tr>
            <td>Time Span</td>
            <td>
              <!-- eslint-disable-next-line vue/no-mutating-props -->
              <select v-model="graph.timeSpan">
                <option v-for="(span, idx) of timeSpans" :key="idx" :value="span">{{span.name}}</option>
              </select>
            </td>
          </tr>
          <tr v-if="graph?.timeSpan?.id == 'custom'">
            <td>Start Date</td>
            <td>
              <!-- eslint-disable-next-line vue/no-mutating-props -->
              <input type="datetime-local" v-model="graph.startDate" />
            </td>
          </tr>
          <tr v-if="graph?.timeSpan?.id == 'custom'">
            <td>End Date</td>
            <td>
              <!-- eslint-disable-next-line vue/no-mutating-props -->
              <input type="datetime-local" v-model="graph.endDate" />
            </td>
          </tr>
          <tr>
            <td>Data Colors</td>
            <td class="graph-color-selection">
              <input type="color" v-model="dataColor1" @change="dataColor1Changed" />
              <input type="color" v-model="dataColor2" v-if="data && data.length > 1" @change="dataColor2Changed" />
            </td>
          </tr>
          <tr if.bind="graph.graphType == 'line' || graph.graphType == 'area'">
            <td>Type</td>
            <td class="graph-type-selection">
              <label>
                <font-awesome-icon :icon="['far', 'chart-line']" title="Line Chart" class="has-cursor-pointer" :class="{ 'has-text-info': graphType == 'line' }" />
                <input type="radio" :name="`chart-${graph?.id}-type`" value="line" v-model="graphType" @change="graphTypeChanged" />
              </label>
              <label>
                <font-awesome-icon :icon="['far', 'chart-area']" title="Area Chart" class="has-cursor-pointer" :class="{ 'has-text-info': graphType == 'area' }" />
                <input type="radio" :name="`chart-${graph?.id}-type`" value="area" v-model="graphType" @change="graphTypeChanged" />
              </label>
            </td>
          </tr>
          <tr>
            <td colspan="2"><button @click="updateGraph()" class="button is-primary is-small" title="Save Graph" :disabled="!canUpdateGraphDates"><b-icon icon="save" size="is-small"></b-icon> <span>Save Graph</span></button></td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="metric-chart-graph">
      <div class="single-chart" :id="'chart-' + graph?.id"></div>
      <span class="no-data" v-if="showNoDataMessage">There is no data for this graph in the selected time range.</span>
      <div class="graphLoader" v-if="loadingGraphData">
        <b-loading :is-full-page="false" :active.sync="loadingGraphData"></b-loading>
      </div>
    </div>
    <div class="metric-chart-summary" v-if="!loadingGraphData && !showNoDataMessage">
      <b-table :data="chartStatistics" :striped="true">        
        <b-table-column field="metricName" label="Metric" v-slot="props" width="250">
          {{ props.row.metricName }}
        </b-table-column>
        <b-table-column field="min" label="Min" v-slot="props" width="40">
          {{ props.row.min }}
        </b-table-column>
        <b-table-column field="max" label="Max" v-slot="props" width="40">
          {{ props.row.max }}
        </b-table-column>
        <b-table-column field="average" label="Average" v-slot="props" width="40">
          {{ props.row.average }}
        </b-table-column>
        <template slot="empty">
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>Nothing here.</p>
            </div>
          </section>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line object-curly-newline
import { sortBy } from 'lodash';
// eslint-disable-next-line object-curly-newline
import { sub, format, roundToNearestMinutes, getTime } from 'date-fns';
import numeral from 'numeral';
import c3 from 'c3';
import ChartComponent from '../../shared/ChartComponent.vue';
import { exportCsvMixin } from '../../shared/mixins/exportCsvMixin';
import { entityTypesParserMixin } from '../../shared/mixins/entityTypesParserMixin';
import { useTerminalsStore } from '../../terminals/terminals-store';

export default {
  props: {
    graph: {
      type: Object,
      default: () => ({
        id: 1, // default id
        timeSpan: null, // how far back to go to get data
        shortcodes: [], // metric shortcodes
        delimiter: null, // use to add zoom level to call
        startDate: null, // used for custom date range
        endDate: null, // used for custom date range
        name: '', // graph's name
        autoRefresh: false, // should this graph pull data every 5 minutes
        dataColor1: '#65BD81',
        dataColor2: '#75A7D3',
        graphType: 'line'
      })
    },
    remoteId: String,
    remoteDevices: Object,
    graphs: Array,
    isTerminal: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      settings: {
        bindto: '',
        data: {
          xs: {},
          columns: [],
          types: {},
          axes: {},
          colors: {},
          names: {}
        },
        regions: [],
        point: {
          show: true,
          r: 2,
          focus: { expand: { r: 5 } }
        },
        zoom: {
          enabled: true, // enable zoom
          rescale: false, // scale y axis when zooming
          // eslint-disable-next-line no-unused-vars
          onzoomend: (domain) => { }
        },
        points: true,
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              format: (x) => format(new Date(x), 'MM/dd HH:mm'),
              culling: { max: 6 }, // show maximum 6 values on x axis, but this actually doesn't work with zooming
              fit: false, // show true fit
              rotate: 15 // labels are rotated slightly cuz zooming...
            },
          },
          y: {},
          y2: { show: false } // be default don't show it
        },
        grid: {
          x: { show: true },
          y: { show: true }
        },
        tooltip: { grouped: true }, // groups values into one tooltip.. turning it off makes it more difficult to view related values
        legend: { show: true },
        spline: {
          interpolation: {
            type: 'monotone'
          }
        },
        line: {
          connectNull: false, // breaks in line graph when data is null
          step: {
            type: 'step-after'
          }
        }
      },
      loadingGraphData: false,
      data: [],
      data1ProviderType: null,
      data2ProviderType: null,
      isFullscreen: false,
      isExportEnabled: false,
      isExporting: false,
      instance: null,
      initialStartDate: null,
      startDate: format(sub(new Date(), { days: 1 }), 'MM/dd/yyyy HH:mm'), // Initiate by default with a day ago
      initialEndDate: null,
      endDate: format(new Date(), 'MM/dd/yyyy HH:mm'), // Initidate by default with today
      metricProvider: null,
      timeSpans: [
        { id: 'halfday', name: 'Last 12 hours' }, 
        { id: 'day', name: 'Last 24 hours' }, 
        { id: 'week', name: 'Last 7 days' }, 
        { id: 'month', name: 'Last 30 days' }, 
        // { id: 'custom', name: 'Custom' }
      ], // List of available timespans
      dataColor1: '',
      dataColor2: '',
      optionsVisible: false,
      initialWidth: 0,
      graphType: 'line',
      stepGraphTypes: [
        'MT_LATITUDE',
        'MT_LONGITUDE',
        'MT_ONLINESTATUS'
      ]
    }
  },
  components: {
    // eslint-disable-next-line vue/no-unused-components
    'c3-chart': ChartComponent
  },
  mixins: [entityTypesParserMixin, exportCsvMixin],
  mounted() {
    if (this.graph.timeSpan.id != 'custom') {
      this.setDates(this.graph.timeSpan);
    } else { // otherwise grab the dates from the graph object
      this.startDate = new Date(this.graph.startDate).toISOString();
      this.endDate = new Date(this.graph.endDate).toISOString();
    }

    this.graphType = this.graph.graphType; // graph type
    this.dataColor1 = this.graph.dataColor1;
    this.dataColor2 = this.graph.dataColor2;
    this.initialTimespan = this.graph.timeSpan.id;
    this.initialStartDate = this.graph.startDate;
    this.initialEndDate = this.graph.endDate;
    this.initialWidth = this.$refs.metricGraph.clientWidth;
    this.getMetrics();
  },
  destroyed() {
    if (this.instance) {
      this.instance = this.instance.destroy();
    }
  },
  computed: {
    metricTypes() {
      if (!this.isTerminal) {
        return this.$store.state.remotes.metricTypes;
      }
      const terminalsStore = useTerminalsStore();
      return terminalsStore.state.metricTypes;
    },
    metricProviders() {
      if (!this.isTerminal) {
        return this.$store.state.remotes.metricProviders;
      }
      const terminalsStore = useTerminalsStore();
      return terminalsStore.state.metricProviders;
    },
    canUpdateGraphDates() {
      return this.graph.timeSpan.id != 'custom' || (this.graph.timeSpan.id == 'custom' && (this.graph.startDate) && (this.graph.endDate));
    },
    showNoDataMessage() {
      // verify we're not fetching graph data
      if (!this.loadingGraphData) {
        // we have some potential data to work with
        if (this.data && this.data.length > 0) {
          // one of the two metrics should have a series
          // if neither of them do, show message
          if (!(this.data[0] && this.data[0].series) && !(this.data[1] && this.data[1].series)) {
            return true;
          }
        } else {
          return true;
        }
      }
      return false;
    },
    remoteHistoricalMetrics() {
      if (!this.isTerminal) {
        return this.$store.state.remotes.remoteHistoricalMetrics;
      }
      const terminalsStore = useTerminalsStore();
      return terminalsStore.state.terminalMetrics;
    },
    chartStatistics() {
      const stats = [];
      if (this.data1ProviderType && this.data[0]) {
        const data1Values = this.data[0].series.map(v => v.y);
        const data1Stats = this.calculateStats(this.data1ProviderType, data1Values);
        stats.push(data1Stats);
      }
      if (this.data2ProviderType && this.data[1]) {
        const data2Values = this.data[1].series.map(v => v.y);
        const data2Stats = this.calculateStats(this.data2ProviderType, data2Values);
        stats.push(data2Stats);
      }

      return stats;
    }
  },
  methods: {
    toggleOptions() {
      this.optionsVisible = !this.optionsVisible;
    },
    calculateStats(providerType, values) {
      let sum = 0;
      let minimum = 0;
      let maximum = 0;
      // iterate through values to parse floats and determine sum of values for the mean (average)
      for (let i = 0; i < values.length; i++) {
        const val = parseFloat(values[i]);
        if (val) { 
          sum += val;

          if (val < minimum) minimum = val;
          if (val > maximum) maximum = val;
        }
      }
      // calculate 'mean' value
      const avg = (sum / values.length) || 0;
      // separate the Metric Shortcode from the Provider
      const metricShortcode = providerType.substring(providerType.indexOf('MT'));

      return { 
        metricName: this.settings.data.names[providerType],
        min: this.formatMetricUnit(metricShortcode, minimum),
        max: this.formatMetricUnit(metricShortcode, maximum),
        average: this.formatMetricUnit(metricShortcode, avg)
      }
    },
    // toggle line or area graph
    // step graph presentation for "Status" hub metrics remains unaffected by this
    graphTypeChanged(newValue) {
      const nv = newValue.target.value;
      const currentMetrics = Object.keys(this.settings.data.types);
      const metricsToChangeType = [];
      currentMetrics.forEach((t) => {
        // adjust the graph type only if the metric type is not a "Status" hub metric
        if (!this.stepGraphTypes.includes(t)) {
          metricsToChangeType.push(t);
        }
      });
      // transform using keys so step graphs aren't affected
      this.instance.transform(this.parseGraphType(nv), metricsToChangeType);
      // toggle icons
      // eslint-disable-next-line vue/no-mutating-props
      this.graph.graphType = nv;
    },
    dataColor1Changed(newValue) {
      if (!this.data || !this.instance) return;
      const colorObject = {};
      const data1 = `${this.data[0].provider}_${this.data[0].type}`;
      const newColor = newValue.target.value;
      colorObject[data1] = newColor;
      // eslint-disable-next-line vue/no-mutating-props
      this.graph.dataColor1 = newColor;
      this.instance.data.colors(colorObject);
    },
    dataColor2Changed(newValue) {
      if (!this.data || !this.instance) return;
      const colorObject = {};
      const data2 = `${this.data[1].provider}_${this.data[1].type}`;
      const newColor = newValue.target.value;
      colorObject[data2] = newColor;
      // eslint-disable-next-line vue/no-mutating-props
      this.graph.dataColor2 = newColor;
      this.instance.data.colors(colorObject);
    },
    // Used for setting dates via the timespan dropdown
    // eslint-disable-next-line no-unused-vars
    updateGraph(transformGraph) {
      // set timespans
      // first check if the timespan changed from initial one
      if (this.graph.timeSpan.id != this.initialTimespan || (this.initialStartDate != this.graph.startDate || this.initialEndDate != this.graph.endDate)) {
        if (this.graph.timeSpan.id != 'custom') {
          this.setDates(this.graph.timeSpan);
          this.initialTimespan = this.graph.timeSpan.id;
        } else {
          this.startDate = new Date(this.graph.startDate).toISOString();
          this.endDate = new Date(this.graph.endDate).toISOString();
          this.initialStartDate = this.graph.startDate;
          this.initialEndDate = this.graph.endDate;
        }
        
        this.getMetrics(); // refresh graph TODO: maybe don't fire this if we are jsut changing color? but.. how to determine..
      }

      this.$store.dispatch('updateSetting', { key: 'userGraphs', value: this.$store.state.user.settings.userGraphs });
      this.optionsVisible = false; // close menu
    },
    goFullScreen() {
      if (!this.instance) return;
      // if it's full screen already, we wanna shrink it
      if (this.isFullscreen == true) {
        const ogWidth = this.initialWidth;
        this.instance.resize({ height: 320, width: ogWidth });
        setTimeout(() => {
          this.isFullscreen = false;
        }, 50);
      } else {
        this.isFullscreen = true;
        setTimeout(() => {
          const height = this.$refs.metricGraph.clientHeight;
          this.instance.resize({ height: height - 220 });
        }, 5);
      }
    },
    setDates(ts) {
      if (ts != null) {
        switch (ts.id) {
          case 'halfday':
            this.startDate = sub(new Date(), { hours: 12 }).toISOString();
            break;
          case 'day':
            this.startDate = sub(new Date(), { days: 1 }).toISOString();
            break;
          case 'week':
            this.startDate = sub(new Date(), { weeks: 1 }).toISOString();
            break;
          case 'month':
            this.startDate = sub(new Date(), { months: 1 }).toISOString();
            break;
          case 'year':
            this.startDate = sub(new Date(), { years: 1 }).toISOString();
            break;
          case 'custom':
            return;
          default:
            break;
        }
      } else { // newly created 
        this.startDate = sub(new Date(), { hours: 12 }).toISOString();
      }

      this.endDate = new Date().toISOString(); // don't need to do this right now since we don't have a custom
    },
    removeGraph(model) {
      this.$emit('remove', model.id);
    },
    async exportGraph() {
      this.isExporting = true;

      const firstMetric = this.data[0];
      const secondMetric = this.data[1];

      // establish headers
      const headers = [];
      headers.push(firstMetric ? `${firstMetric.provider} ${firstMetric.type} TIMESTAMP` : 'N/A');
      headers.push(firstMetric ? `${firstMetric.provider} ${firstMetric.type} VALUE` : 'N/A');
      headers.push(secondMetric ? `${secondMetric.provider} ${secondMetric.type} TIMESTAMP` : 'N/A');
      headers.push(secondMetric ? `${secondMetric.provider} ${secondMetric.type} VALUE` : 'N/A');

      const rowContent = [];
      let firstSeries = [];

      // fill the 1st and 2nd columns with the first metric series data
      if (firstMetric) {
        firstSeries = firstMetric.series;
        firstSeries.forEach(element => {
          // fill first two columns
          rowContent.push([
            new Date(element.x).toISOString(),
            element.y
          ]);
        });
      }

      // fill the 3rd and 4th columns with the second metric series data
      if (secondMetric) {
        const secondSeries = secondMetric.series || [];      
        secondSeries.forEach((element, index) => {
          // check rowContent to see if index exists
          // if we already have data at this index, we need to append and retain original values
          if (rowContent[index]) {
            rowContent[index] = [
              // keep the values in place for the first series, or empty if not present
              firstSeries[index] ? new Date(firstSeries[index].x).toISOString() : '',
              firstSeries[index] ? firstSeries[index].y : '',
              // insert second metric values to their respective columns
              new Date(element.x).toISOString(),
              element.y
            ];
          } else { // 
            rowContent.push([
              '', // keep the values in place for the first series, or empty if not present
              '',
              new Date(element.x).toISOString(),
              element.y
            ]);
          }
        });
      }

      // create csv and initiate download
      if (rowContent.length > 0) {
        await this.exportCsvArray(headers, rowContent, 'metrics-export');
      }
      
      this.isExporting = false;
    },
    async fetchData(graph) {
      const metricsData = [];
      // if no shortcodes were provided
      if (!graph || !graph.shortcodes) return metricsData;
      // determine the origin of each shortcode so you can pull data from correct service
      // eslint-disable-next-line no-restricted-syntax, no-unused-vars
      for (const [type, key, value] of graph.shortcodes) {
        const payload = {
          metrics: [key], // key == shortcode
          startDate: this.startDate,
          endDate: this.endDate,
          provider: type
        };
        
        let success = false;
        if (!this.isTerminal) { // TODO: this can be removed later when remotes are gone
          payload.remoteIds = [this.remoteId];
          // fetch from Remotes service
          // eslint-disable-next-line no-await-in-loop
          success = await this.$store.dispatch('fetchRemoteHistoricalMetrics', payload);
        } else {
          payload.terminalIds = [this.remoteId];
          const terminalsStore = useTerminalsStore();
          // eslint-disable-next-line no-await-in-loop
          success = await terminalsStore.dispatch('fetchMetrics', payload);
        }

        if (!success) {
          // show toast to indicate error
          this.$buefy.toast.open({ 
            message: 'Encountered an error while fetching metric data.',
            type: 'is-danger', 
          });
          // eslint-disable-next-line no-continue
          continue;
        }

        // verify the vuex store has data
        // eslint-disable-next-line no-continue
        if (!this.remoteHistoricalMetrics || !this.remoteHistoricalMetrics[0]) continue;
        const data = this.remoteHistoricalMetrics[0];
        
        // verify we have metrics data to chart
        // eslint-disable-next-line no-continue
        if (!data || !data.metrics[0]) continue;
        const metricResult = data.metrics[0];
        const { values } = metricResult;
        
        // transform data to an object model that the grid can leverage
        const metricGraphData = {
          provider: type,
          type: key, // shortcode
          labels: values.map(v => getTime(new Date(v.timestamp))),
          series: values.map(v => ({
            x: getTime(new Date(v.timestamp)),
            // eslint-disable-next-line no-nested-ternary
            y: this.getValue(key, v.value)
          })),
          tolerance: 80 // minutes
        }
        
        // append to the return object
        metricsData.push(metricGraphData);
      }
      return metricsData;
    },
    async getMetrics() {
      this.loadingGraphData = true;

      // fetch the Metrics data
      this.data = await this.fetchData(this.graph);

      if (this.data && this.data.length > 0) {
        // object to hold parsed/massaged data in use by graphs
        const graphData = {
          x1: ['x1'],
          x2: ['x2'],
          y: [],
          y2: []
        }

        const primaryData = this.data[0];
        const secondaryData = this.data[1];

        // exit early if no series
        if (!(primaryData && primaryData.series) && !(secondaryData && secondaryData.series)) {
          this.loadingGraphData = false;
          return;
        }

        // enable the ability to export
        this.isExportEnabled = true;

        const hasTwoSeries = !!((primaryData && primaryData.series) && (secondaryData && secondaryData.series));
        if (primaryData.series) {
          this.setGraphType(primaryData);
          this.processMetric(primaryData, 'y', graphData, hasTwoSeries);
          this.data1ProviderType = `${primaryData.provider}_${primaryData.type}`;
          
          // Y-Axis label with metric type
          this.settings.axis.y.label = this.getUnitFromData(primaryData);
        }
        if (secondaryData && secondaryData.series) {
          this.setGraphType(secondaryData);
          // when no first series exists, use standard "y" as yAxis for secondary
          const yAxis = hasTwoSeries ? 'y2' : 'y';
          this.processMetric(secondaryData, yAxis, graphData, hasTwoSeries);
          this.data2ProviderType = `${secondaryData.provider}_${secondaryData.type}`;
        }

        // compare the units to see if we need to render the y2 axis ticks
        if (secondaryData && secondaryData.type) {
          // if there's a second metric, always show the y2 ticks
          this.settings.axis.y2.show = true;
          this.settings.axis.y2.tick = {
            format: (d) => this.formatMetricUnit(secondaryData.type, d)
          }

          // Secondary Y-Axis label with metric type
          this.settings.axis.y2.label = this.getUnitFromData(secondaryData);
        }

        /* There's a known issue with c3-chart region onZoom, https://github.com/c3js/c3/issues/1357
        // for now, we are not showing any regions, but plain white space for missing data in metric charts
        // keeping this code commented, if we found potential fix in future
        // create regions for gaps in data
        if (!hasTwoSeries) {
          this.createRegions(this.settings.data.columns[0], this.settings.data.columns[1]);
        } else {
          this.createRegions(this.settings.data.columns[0], this.settings.data.columns[2]);
          this.createRegions(this.settings.data.columns[1], this.settings.data.columns[3]);
        }
        */

        // bind to the element
        this.settings.bindto = `#chart-${this.graph.id}`;
      }

      // destroy existing first
      if (this.instance) {
        this.instance = this.instance.destroy();
      }

      // console.log('[getMetrics] settings: ', this.settings);
      // BOOM! Make the graph.
      this.instance = c3.generate(this.settings);

      // If we're in full screen, make sure the graph is resized after generation
      if (this.isFullscreen) {
        const height = this.$refs.metricGraph.clientHeight;
        this.instance.resize({ height: height - 60 });
      }

      this.loadingGraphData = false;
    },
    processMetric(data, yAxis, graphData, hasTwoSeries) {
      if (!data.series || !graphData || !yAxis) {
        return;
      }

      const xAxis = yAxis == 'y' ? 'x1' : 'x2'; 
      const beginning = data.series[0].x;
      const end = data.series[data.series.length - 1].x;
      const interval = (data.provider == 'PV_ANTENNA') ? 60 : 5; // minutes
      const iteration = interval * 60 * 1000;
      const { tolerance } = data; // minutes
      let idx = 0;

      // set initial array element based on type
      // eslint-disable-next-line no-param-reassign
      graphData[yAxis][0] = `${data.provider}_${data.type}`;

      // force sort on metric data (using timestamp)
      // eslint-disable-next-line no-param-reassign
      data.series = sortBy(data.series, ['x']);

      // use data.type to determine which sub-type of metric it is
      if (this.stepGraphTypes.includes(data.type)) {
        // plot the data we get back without any interval expectation
        data.series.forEach(d => {
          // push values to both axis (no regions for this category)
          graphData[xAxis].push(d.x);
          graphData[yAxis].push(d.y);
        });
      } else {
        switch (data.provider) {
          case 'PV_ANTENNA':
            // iterate through data.series
            for (let i = 0; i < data.series.length; i++) {
              // determine distance (time) to next point
              const distance = (i < data.series.length - 1) 
                ? (data.series[i + 1].x - data.series[i].x) / 60 / 1000 // calculate the different between timestamps to the next point
                : 0;
              
              // plot current point
              graphData[xAxis].push(data.series[i].x);
              graphData[yAxis].push(data.series[i].y || null); // set null if y value is undefined

              // when distance is greater than tolerance, insert a null so a region can be formed
              if (distance > tolerance) {
                graphData[xAxis].push(data.series[i].x + interval);
                graphData[yAxis].push(null);
              }
            }
            break;
          case 'PV_INTSATFLEXMOVE':
            // iterate through timeseries based on interval
            for (let i = beginning; i <= end; i += iteration) {
              const seriesDate = this.getRoundedDate(5, new Date(data.series[idx].x)).toISOString();
              const iterationDate = this.getRoundedDate(5, new Date(i)).toISOString();

              // push timestamp to x-axis
              graphData[xAxis].push(i);

              if (seriesDate == iterationDate) {
                graphData[yAxis].push(data.series[idx++].y);
              } else {
                graphData[yAxis].push(null);
              }
            }
            break;
          default:
            // PV_KYMETABROADBAND
            // PV_CELLULAR
            // plot the data we get back without any interval expectation
            data.series.forEach(d => {
              // push values to both axis (no regions for this category)
              graphData[xAxis].push(d.x);
              graphData[yAxis].push(d.y || null); // set null if y value is undefined
            });
            break;
        }
      }
      
      // get the column context for the graph based on how many metrics are present
      let col = null;

      if (hasTwoSeries) {
        col = yAxis == 'y' ? 2 : 3;
      } else {
        col = yAxis == 'y' ? 1 : 2;
      }
      
      // set the data for xAxis
      this.settings.data.columns[xAxis == 'x1' ? 0 : 1] = graphData[xAxis];
      // set the data for yAxis
      this.settings.data.columns[col] = graphData[yAxis];
      // set yAxis
      this.settings.data.axes[`${data.provider}_${data.type}`] = yAxis;
      // set x-axis context
      this.settings.data.xs[`${data.provider}_${data.type}`] = yAxis == 'y' ? 'x1' : 'x2';
      // set color
      this.settings.data.colors[`${data.provider}_${data.type}`] = yAxis == 'y' ? this.graph.dataColor1 : this.graph.dataColor2;

      // tooltip formatting
      this.settings.tooltip.format = {
        value: (value, ratio, id) => {
          // separate the Metric Shortcode from the Provider
          const metricShortcode = id.substring(id.indexOf('MT'));
          return this.formatMetricUnit(metricShortcode, value);
        }
      }
      // y ticks formatting
      this.settings.axis[yAxis].tick = {
        format: (d) => this.formatMetricUnit(data.type, d)
      }

      // set names for legend
      this.settings.data.names[`${data.provider}_${data.type}`] = this.getLabelName(data.type, data.provider);
    },
    /* There's a known issue with c3-chart region onZoom, https://github.com/c3js/c3/issues/1357
    // for now, we are not showing any regions, but plain white space for missing data in metric charts
    // keeping this code commented, if we found potential fix in future
    createRegions(xAxis, yAxis) {
      let regionStart = null;
      let regionEnd = null;

      // no yAxis data - break out of the function
      if (!yAxis || yAxis.length <= 0) {
        return;
      }

      // regions
      for (let i = 1; i < yAxis.length; i++) {
        const el = yAxis[i];
        if (el == null) {
          if (!regionStart) {
            regionStart = xAxis[i - 1];
          }
        } else if (regionStart) {
          regionEnd = xAxis[i]; // set the end date
          this.settings.regions.push({ 
            axis: 'x', 
            start: regionStart, 
            end: regionEnd, 
            class: 'missing-data' 
          }); // push region into settings
          regionStart = null; // null out the region start
          regionEnd = null; // null out region end
        }
      }
    },
    */
    setGraphType(data) {
      // show step graph for all "Status" type hub metrics
      // default for other types
      this.settings.data.types[`${data.provider}_${data.type}`] = this.stepGraphTypes.includes(data.type)
        ? 'step'
        : this.parseGraphType(this.graph.graphType);
    },
    getLabelName(metricShortcode, providerShortcode) {
      // find metric types specific to the provider
      const providerMetrics = this.metricTypes.find(mt => mt.providerShortcode == providerShortcode);
      // verify we have types for the provider
      if (!providerMetrics || !providerMetrics.types || providerMetrics.types.length == 0) return metricShortcode;
      const provider = this.metricProviders.find(mp => mp.shortcode == providerShortcode);
      // find the specific metric
      const metric = providerMetrics.types.find(pmt => pmt.shortcode == metricShortcode);
      // no match found, return just the shortcode
      if (!metric) return metricShortcode;
      // we have a match, so return the name
      return `${metric.name} (${provider.name})`;
    },
    parseUnit(metricType) {
      switch (metricType) {
        case 'MT_LATITUDE':
        case 'MT_LONGITUDE':
        case 'ASM-DIRECTION':
        case 'ASM-LATITUDE':
        case 'ASM-LONGITUDE':
        case 'ASM-RX-LPA':
        case 'ASM-TX-PHI':
        case 'ASM-TX-THETA':
        case 'ASM-TX-LPA':
        case 'ASM-RX-PHI':
        case 'ASM-RX-THETA':
        case 'ASM-THETA-RX':
        case 'ASM-THETA-TX':
        case 'ASM-PHI-RX':
        case 'ASM-PHI-TX':
        case 'ASM-LPA-RX':
        case 'ASM-LPA-TX':
        case 'ASM-TARGET-LONGITUDE':
        case 'ASM-TARGET-LATITUDE':
        case 'ASM-TARGET-POLARIZATION-RX':
        case 'ASM-TARGET-POLARIZATION-TX':
          return '°';
        case 'ASM-SPEED':
          return 'km/h';
        case 'ASM-ALTITUDE':
        case 'ASM-TARGET-ALTITUDE':
          return 'm';
        case 'ASM-TARGET-SYMBOL-RATE-MSYMS':
          return 'MSymbol/s';
        case 'ASM-SENSOR-1.1V':
        case 'ASM-SENSOR-1.5V':
        case 'ASM-SENSOR-2.5V':
        case 'ASM-SENSOR-3.3V':
        case 'ASM-SENSOR-5V':
        case 'ASM-SENSOR-20V':
        case 'ASM-SENSOR-ANALOG-VOLTAGE':
        case 'ASM-BOARD-INPUT-VOLTAGE':
        case 'ASM-CLOCK-VOLTAGE':
        case 'ASM-GATE-VOLTAGE':
        case 'ASM-HEATER-CONTROL-VOLTAGE':
        case 'ASM-SOURCE-VOLTAGE':
        case 'ASM-WAVEGUIDE-THERMISTOR':
        case 'ASM-HEATER-VOLTAGE':
          return 'volts';
        case 'ASM-BOARD-INPUT-CURRENT':
        case 'ASM-GATE-CURRENT':
        case 'ASM-SOURCE-CURRENT':
        case 'ASM-HEATER-CURRENT':
          return 'amps';
        // case 'MT_PACKETLOSS':
        case 'MT_JITTER':
        case 'ASM-HUMIDITY-RELATIVE':
          return '%';
        case 'DATABURSTCRCERR':
          return 'errors';
        case 'MT_ACQBURSTCOUNT':
        case 'MT_ACQBURSTCRCERR':
        case 'MT_ACQBURSTMISSMTCH':
        case 'MT_ACQBURSTMISSING':
        case 'MT_DATABURSTCOUNT':
        case 'MT_DATABURSTMISSING':
          return 'bursts';
        case 'MT_LATENCY':
          return 'ms';
        case 'MT_SSPCDOWN':
        case 'MT_SSPCUP':
        case 'MT_TCPDATAVOLRCV':
        case 'MT_TPCDATAVOLTRSM':
        case 'MT_UDPDATAVOLRCV':
        case 'MT_UDPDATAVOLTRSM':
        case 'MT_ICMPDATAVOLRCV':
        case 'MT_ICMPDATAVOLTRSM':
        case 'MT_IGMPDATAVOLRCV':
        case 'MT_IGMPDATAVOLTRSM':
        case 'MT_HTTPDATAVOLRCV':
        case 'MT_HTTPDATAVOLTRSM':
        case 'MT_USAGE':
          return 'bytes';
        case 'MT_UPSTREAM_THROUGHPUT':
        case 'MT_DOWNSTREAM_THROUGHPUT':
          return 'kbps';
        case 'MT_TERMDOWNMINSNR':
        case 'MT_PWRADJUSTMENT':
        case 'MT_RCVPWR':
          return 'dBm';
        case 'MT_TERMDOWNAVGSNR':
        case 'MT_CURRENTSNR':
        case 'MT_PWRHEADROOM':
        case 'MT_MAXSNRDOWN':
        case 'ASM-R-ESNO':
        case 'ASM-PL-ESNO':
          return 'dB';
        case 'MT_MAXCN0UP':
          return 'dBHz';
        case 'MT_ONLINETIME':
        case 'MT_TERMUPTIME':
          return 'sec';
        case 'MT_LOSTRCVLOCKCOUNT':
          return 'locks';
        case 'MT_TIMESLOTS':
        case 'MT_FREETIMESLOTS':
          return 'slots';
        case 'MT_TPCSESSIONS':
          return 'sessions';
        case 'MT_BOARDTEMP':
        case 'ASM-THERMISTOR-Q1':
        case 'ASM-THERMISTOR-Q2':
        case 'ASM-THERMISTOR-Q3':
        case 'ASM-THERMISTOR-Q4':
        case 'ASM-TEMPERATURE':
          return '°C';
        case 'MT_PACKETSRCV':
        case 'MT_PACKETSTRSM':
          return 'pkt';
        case 'MT_FREQOFFSET':
        case 'MT_RCVCRROFFSETFREQ':
        case 'ASM-ANTENNA-FREQUENCY-RX':
        case 'ASM-ANTENNA-FREQUENCY-TX':
          return 'Hz';
        case 'MT_CNGSTNCOUNT':
          return 'occurances';
        default:
          return '';
      }
    },
    formatMetricUnit(metricType, value) {
      switch (metricType) {
        case 'MT_SSPCDOWN':
        case 'MT_SSPCUP':
        case 'MT_TCPDATAVOLRCV':
        case 'MT_TPCDATAVOLTRSM':
        case 'MT_UDPDATAVOLRCV':
        case 'MT_UDPDATAVOLTRSM':
        case 'MT_ICMPDATAVOLRCV':
        case 'MT_ICMPDATAVOLTRSM':
        case 'MT_IGMPDATAVOLRCV':
        case 'MT_IGMPDATAVOLTRSM':
        case 'MT_HTTPDATAVOLRCV':
        case 'MT_HTTPDATAVOLTRSM':
        case 'MT_UPSTREAM_THROUGHPUT':
        case 'MT_DOWNSTREAM_THROUGHPUT':
          return numeral(value).format('0.00 b');
        case 'MT_TERMDOWNMINSNR':
        case 'MT_TERMDOWNAVGSNR':
        case 'MT_CURRENTSNR':
        case 'MT_BOARDTEMP':
        case 'MT_RVALUE':
        case 'MT_MAXCN0UP':
        case 'MT_MAXSNRDOWN':
        case 'MT_PWRHEADROOM':
        case 'MT_LATITUDE':
        case 'MT_LONGITUDE':
        case 'MT_PWRADJUSTMENT':
        case 'MT_RCVPWR':
        case 'ASM-DIRECTION':
        case 'ASM-LATITUDE':
        case 'ASM-LONGITUDE':
        case 'ASM-RX-LPA':
        case 'ASM-TX-PHI':
        case 'ASM-TX-THETA':
        case 'ASM-TX-LPA':
        case 'ASM-RX-PHI':
        case 'ASM-RX-THETA':
        case 'ASM-THETA-RX':
        case 'ASM-THETA-TX':
        case 'ASM-PHI-RX':
        case 'ASM-PHI-TX':
        case 'ASM-LPA-RX':
        case 'ASM-LPA-TX':
        case 'ASM-TARGET-LONGITUDE':
        case 'ASM-TARGET-LATITUDE':
        case 'ASM-TARGET-POLARIZATION-RX':
        case 'ASM-TARGET-POLARIZATION-TX':
        case 'ASM-THERMISTOR-Q1':
        case 'ASM-THERMISTOR-Q2':
        case 'ASM-THERMISTOR-Q3':
        case 'ASM-THERMISTOR-Q4':
        case 'ASM-TEMPERATURE':
        case 'ASM-SENSOR-1.1V':
        case 'ASM-SENSOR-1.5V':
        case 'ASM-SENSOR-2.5V':
        case 'ASM-SENSOR-3.3V':
        case 'ASM-SENSOR-5V':
        case 'ASM-SENSOR-20V':
        case 'ASM-SENSOR-ANALOG-VOLTAGE':
        case 'ASM-BOARD-INPUT-VOLTAGE':
        case 'ASM-CLOCK-VOLTAGE':
        case 'ASM-GATE-VOLTAGE':
        case 'ASM-HEATER-CONTROL-VOLTAGE':
        case 'ASM-SOURCE-VOLTAGE':
        case 'ASM-WAVEGUIDE-THERMISTOR':
        case 'ASM-HEATER-VOLTAGE':
        case 'ASM-BOARD-INPUT-CURRENT':
        case 'ASM-GATE-CURRENT':
        case 'ASM-SOURCE-CURRENT':
        case 'ASM-HEATER-CURRENT':
        case 'ASM-R-ESNO':
        case 'ASM-PL-ESNO':
          return `${numeral(value).format('0.00')} ${this.parseUnit(metricType)}`;
        case 'MT_TPCSESSIONS':
        case 'MT_ACQBURSTCOUNT':
        case 'MT_ACQBURSTCRCERR':
        case 'MT_ACQBURSTMISSMTCH':
        case 'MT_ACQBURSTMISSING':
        case 'MT_DATABURSTCRCERR':
        case 'MT_FREQOFFSET':
        case 'MT_LOSTRCVLOCKCOUNT':
        case 'MT_RCVCRROFFSETFREQ':
        case 'MT_TIMESLOTS':
        case 'MT_FREETIMESLOTS':
        case 'MT_PACKETLOSS':
        case 'MT_ONLINETIME':
        case 'MT_CNGSTNCOUNT':
        case 'ASM-SPEED':
        case 'ASM-ALTITUDE':
        case 'ASM-TARGET-ALTITUDE':
        case 'ASM-HUMIDITY-RELATIVE':
        case 'ASM-ANTENNA-FREQUENCY-RX':
        case 'ASM-ANTENNA-FREQUENCY-TX':
          return `${value} ${this.parseUnit(metricType)}`;
        case 'MT_PACKETSRCV':
        case 'MT_PACKETSTRSM':
        case 'MT_DATABURSTCOUNT':
        case 'MT_DATABURSTMISSING':
        case 'MT_JITTER':
        case 'MT_LATENCY':
          return `${numeral(value).format('0,0')} ${this.parseUnit(metricType)}`;
        case 'MT_TERMUPTIME':
          return numeral(value).format('00:00:00');
        default:
          return numeral(value).format('0.00[0]');
      }
    },
    parseGraphType(type) {
      switch (type) {
        case 'line':
          return 'spline';
        case 'area':
          return 'area-spline';
        case 'pie':
          return 'pie';
        case 'bar':
          return 'bar';
        case 'step':
          return 'step';
        default:
          return 'line';
      }
    },
    getRoundedDate(minutes, d = new Date()) {
      return roundToNearestMinutes(new Date(d), { nearestTo: minutes });
    },
    getUnitFromData(data) {
      const providerMetrics = this.metricTypes.filter(m => m.providerShortcode == data?.provider)[0];
      if (providerMetrics) {
        const providerTypes = providerMetrics.types?.filter(md => md.shortcode == data?.type)[0];
        if (providerTypes) {
          const unit = providerTypes.unit;
          if (unit && unit != 'TBD' && unit != 'N/A') return unit;
        }  
      }
      return '';
    },
    getValue(key, value) {
      // special handling for Online Status
      if (key == 'MT_ONLINESTATUS') {
        return value == 1 || value == 6 ? 1 : 0;
      }
      // Boolean metrics
      if (value.toLowerCase() == 'true') {
        return '1';
      } else if (value.toLowerCase() == 'false') {
        return '0';
      }
      return value; // regular metric
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../node_modules/c3/c3.min.css";
@import "../../styles/_colors";

.metric-chart-wrapper {
  position: relative;
  min-height: 300px;
  overflow: hidden;

  &.full-screen {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    padding: 25px;
    z-index: 999;
    background: $white;
    margin-top: 0;
    margin-bottom: 0;
  }

  &:last-child {
    border-bottom: 0;
  }

  .metric-chart-meta {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;

    .metric-chart-name {
      align-items: center;
      display: flex;

      h5 {
        font-weight: bold;
        color: $grey;
      }
    }

    .metric-chart-actions {
      display: flex;

      & > * {
        margin-left: 5px;
      }
    }
  }

  .metric-chart-dropdown {
    display: flex;
    position: absolute;
    right: 0;
    background: $white;
    z-index: 2;
    padding: 10px;

    table {
      width: 100%;

      td {
        padding: 5px;

        select,
        input[type="datetime-local"] {
          width: 100%;
        }

        button {
          float: right;
        }

        &.graph-color-selection {
          display: flex;

          input[type="color"] {
            &:first-child {
              margin-right: 5px;
            }
          }
        }

        &.graph-type-selection {
          font-size: 18px;

          label:first-child {
            padding-left: 0px;
          }
          label {
            padding-left: 10px;
          }

          input[type="radio"] {
            display: none;
          }
        }
      }
    }
  }

  .metric-chart-graph {
    &::v-deep .missing-data {
      background-color: $red;
      fill: $red;
    }

    .graphLoader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: white;
      opacity: 0.7;
    }

    .no-data {
      color: $grey;
    }
  }
}
</style>
